import {
  collection,
  doc,
  serverTimestamp,
  type CollectionReference,
  type DocumentData,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { ZodError } from 'zod'
import type {
  FirestoreOrganizationCustomProduct,
  FirestoreOrganizationCustomProductWrite,
} from './schema'
import { schema } from './schema'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import type { StreamInterface } from 'tricklejs/dist/types'
import { modelListStream } from '../../firestore-mobx/stream'
import { OrganizationCustomProduct } from '../../models/OrganizationCustomProduct'
import {
  deleteDocWithError,
  setDocWithError,
  getDocsWithError,
} from '../../firestore-mobx/fetch'
import { convertDocumentSnapshotToModel } from '../../firestore-mobx/stream'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreOrganizationCustomProduct> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    try {
      return schema.parse(data)
    } catch (e) {
      if (e instanceof ZodError) {
        console.error('Error parsing organization custom product', e.errors)
      }
      throw e
    }
  },
}

const getColRef = (
  firestore: Firestore,
  organizationId: string
): CollectionReference<FirestoreOrganizationCustomProduct, DocumentData> => {
  return collection(
    firestore,
    'organization',
    organizationId,
    'custom_product'
  ).withConverter(converter)
}

export const getOrganizationCustomProducts = (
  repository: FirebaseRepository,
  params: { organizationId: string }
): StreamInterface<OrganizationCustomProduct[]> => {
  const ref = getColRef(repository.firestore, params.organizationId)

  return modelListStream(repository, ref, OrganizationCustomProduct)
}

export const createOrganizationCustomProduct = async (
  repository: FirebaseRepository,
  params: {
    organizationId: string
    productName: string
    sectionPassQuantity: number
    sectionPassPrice: number
    expiresAt?: Date
    productType: string
  }
): Promise<string> => {
  const ref = getColRef(repository.firestore, params.organizationId)
  const docRef = doc(ref)

  const data: FirestoreOrganizationCustomProductWrite = {
    productId: docRef.id,
    organizationId: params.organizationId,
    creatorUserId: repository.uid,
    productName: params.productName,
    productType: params.productType,
    sectionPassQuantity: params.sectionPassQuantity,
    sectionPassPrice: params.sectionPassPrice,
    expiresAt: params.expiresAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  await setDocWithError(docRef, data)
  return docRef.id
}

export const updateOrganizationCustomProduct = async (
  repository: FirebaseRepository,
  params: {
    organizationId: string
    productId: string
    productName?: string
    sectionPassQuantity?: number
    sectionPassPrice?: number
    expiresAt?: Date
    productType?: string
  }
): Promise<void> => {
  const { organizationId, productId, ...rest } = params
  const ref = getColRef(repository.firestore, organizationId)
  const docRef = doc(ref, productId)

  const updateData: Partial<FirestoreOrganizationCustomProductWrite> = {
    ...rest,
    creatorUserId: repository.uid,
    updatedAt: serverTimestamp(),
  }

  await setDocWithError(docRef, updateData, { merge: true })
}

export const removeOrganizationCustomProduct = async (
  repository: FirebaseRepository,
  params: {
    organizationId: string
    productId: string
  }
) => {
  const ref = doc(
    getColRef(repository.firestore, params.organizationId),
    params.productId
  )
  return deleteDocWithError(ref)
}

export const fetchOrganizationCustomProducts = async (
  repository: FirebaseRepository,
  organizationId: string
): Promise<OrganizationCustomProduct[]> => {
  const ref = getColRef(repository.firestore, organizationId)
  const docs = await getDocsWithError(
    ref,
    'FetchOrganizationCustomProductsError'
  )

  return docs.docs.map((doc) =>
    convertDocumentSnapshotToModel(repository, doc, OrganizationCustomProduct)
  )
}
